import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Spin, Switch, Typography, Input, InputNumber, message, Alert, Tooltip, Select } from "antd";
import { SettingOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  updateOrganization,
} from "../../redux/organizationSlice";

import CustomCssTransition from "../UI/CustomCssTransition/customCssTransition";
import SaveButton from "../UI/savebutton";
import DOMPurify from 'dompurify';
import { formatPhoneNumber } from "../../utils/utilFunctions";

const { Title } = Typography;
const { Option } = Select;

const poppinsStyle = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
`;

function CapacitySettings() {
  const { organizationData, loading } = useSelector(
    (state) => state?.organization
  );
  const defaultMessage = "There are no openings remaining for this date. Please select a different date, or call us if this is an emergency.";
  const defaultRequestBookingButtonText = "Request Booking";
  const defaultRequestBookingExplainerText = "If this is the only date you are available, please press the button above and we will reach out to you immediately!";

  const [editorContent, setEditorContent] = useState(organizationData?.noTimeslotsAvailableMessage || defaultMessage);
  const [isDirty, setIsDirty] = useState(false);
  const [useDefaultText, setUseDefaultText] = useState(false);
  const focus = useRef(null);
  const mainContentRef = useRef(null);

  const dispatch = useDispatch();


  const { setValue, watch, reset } = useForm({
    defaultValues: {
      isSTCapacitySet: organizationData?.isSTCapacitySet || false,
      isCapacityUnthrottled: organizationData?.isCapacityUnthrottled || false,
      showNextAvailableSlot: organizationData?.showNextAvailableSlot || false,
      showNonJobsEvents: organizationData?.showNonJobsEvents || false,
      bufferSettings: organizationData?.bufferSettings || {
        preventPastSlotSelection: false,
        requireBufferTime: false,
        bufferHours: 0,
      },
      requestBookingSettings: organizationData?.requestBookingSettings || {
        isEnabled: false,
        buttonText: defaultRequestBookingButtonText,
        explainerText: defaultRequestBookingExplainerText,
        notificationEmails: [],
      },
    },
  });

  const isSTCapacitySet = watch("isSTCapacitySet");
  const isCapacityUnthrottled = watch("isCapacityUnthrottled");
  const showNextAvailableSlot = watch("showNextAvailableSlot");
  const showNonJobsEvents = watch("showNonJobsEvents");
  const bufferSettings = watch("bufferSettings");
  const preventPastSlotSelection = bufferSettings?.preventPastSlotSelection || false;
  const requireBufferTime = bufferSettings?.requireBufferTime || false;
  const bufferHours = bufferSettings?.bufferHours || 0;
  const requestBookingSettings = watch("requestBookingSettings");
  const isRequestBookingEnabled = requestBookingSettings?.isEnabled ?? false;
  const requestBookingButtonText = requestBookingSettings?.buttonText || defaultRequestBookingButtonText;
  const requestBookingExplainerText = requestBookingSettings?.explainerText || defaultRequestBookingExplainerText;
  const requestBookingNotificationEmails = requestBookingSettings?.notificationEmails || [];

  useEffect(() => {
    if (organizationData) {
      // Handle backward compatibility: if old fields exist, migrate to new object structure
      const requestBookingConfig = organizationData?.requestBookingSettings || {
        isEnabled: organizationData?.isRequestBookingEnabled ?? false,
        buttonText: organizationData?.requestBookingButtonText || defaultRequestBookingButtonText,
        explainerText: organizationData?.requestBookingExplainerText || defaultRequestBookingExplainerText,
        notificationEmails: organizationData?.requestBookingNotificationEmails || [],
      };

      reset({
        isSTCapacitySet: organizationData?.isSTCapacitySet || false,
        isCapacityUnthrottled: organizationData?.isCapacityUnthrottled || false,
        showNextAvailableSlot: organizationData?.showNextAvailableSlot || false,
        showNonJobsEvents: organizationData?.showNonJobsEvents || false,
        bufferSettings: organizationData?.bufferSettings || {
          preventPastSlotSelection: false,
          requireBufferTime: false,
          bufferHours: 0,
        },
        requestBookingSettings: requestBookingConfig,
      });
      const hasCustomMessage = organizationData?.noTimeslotsAvailableMessage && organizationData.noTimeslotsAvailableMessage !== defaultMessage;
      setUseDefaultText(!hasCustomMessage);
      setEditorContent(hasCustomMessage ? organizationData.noTimeslotsAvailableMessage : defaultMessage);
    }
  }, [organizationData, reset]);

  const handleSwitchChange = (checked) => {
    setValue("isSTCapacitySet", checked, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleUnthrottledSwitchChange = (checked) => {
    setValue("isCapacityUnthrottled", checked, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleShowNextAvailableSlotChange = (checked) => {
    setValue("showNextAvailableSlot", checked, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleShowNonJobsEventsChange = (checked) => {
    setValue("showNonJobsEvents", checked, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handlePreventPastSlotSelectionChange = (checked) => {
    setValue("bufferSettings", {
      ...bufferSettings,
      preventPastSlotSelection: checked,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleRequireBufferTimeChange = (checked) => {
    setValue("bufferSettings", {
      ...bufferSettings,
      requireBufferTime: checked,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleBufferHoursChange = (value) => {
    setValue("bufferSettings", {
      ...bufferSettings,
      bufferHours: value || 0,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    setEditorContent(newValue);
    setIsDirty(true);
  };

  const handleDefaultToggle = (checked) => {
    setUseDefaultText(checked);
    if (checked) {
      setEditorContent(defaultMessage);
    }
    setIsDirty(true);
  };

  const handleRequestBookingButtonTextChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 30) {
      setValue("requestBookingSettings", {
        ...requestBookingSettings,
        buttonText: newValue,
      }, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setIsDirty(true);
    }
  };

  const handleRequestBookingExplainerTextChange = (e) => {
    const newValue = e.target.value;
    setValue("requestBookingSettings", {
      ...requestBookingSettings,
      explainerText: newValue,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleRequestBookingNotificationEmailsChange = (value) => {
    setValue("requestBookingSettings", {
      ...requestBookingSettings,
      notificationEmails: value,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setIsDirty(true);
  };

  const handleRequestBookingEnabledChange = (checked) => {
    setValue("requestBookingSettings", {
      ...requestBookingSettings,
      isEnabled: checked,
    }, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (checked) {
      setTimeout(() => {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTo({ top: mainContentRef.current.scrollHeight, behavior: 'smooth' });
        }
      }, 300);
    }
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!useDefaultText && !editorContent?.trim()) {
      message.error('Please enter a message or use the default message');
      return;
    }

    const messageToSave = useDefaultText ? defaultMessage : editorContent.trim();

    if (requireBufferTime && (!bufferHours || bufferHours <= 0)) {
      message.error('Please enter a valid buffer time in hours');
      return;
    }

    dispatch(
      updateOrganization({
        id: organizationData?._id,
        payload: {
          ...organizationData,
          isCapacityUnthrottled,
          noTimeslotsAvailableMessage: messageToSave,
          showNextAvailableSlot: showNextAvailableSlot,
          showNonJobsEvents: showNonJobsEvents,
          isSTCapacitySet,
          bufferSettings: {
            preventPastSlotSelection,
            requireBufferTime,
            bufferHours: requireBufferTime ? bufferHours : 0,
          },
          requestBookingSettings: {
            isEnabled: isRequestBookingEnabled,
            buttonText: requestBookingButtonText.trim() || defaultRequestBookingButtonText,
            explainerText: requestBookingExplainerText.trim() || defaultRequestBookingExplainerText,
            notificationEmails: requestBookingNotificationEmails,
          },
        },
        page: "Capacity Settings"
      })
    );
  };

  const steps = [
    { label: "CLIENT INFO", isCompleted: true },
    { label: "PROBLEM", isCompleted: true },
    { label: "DESCRIPTION", isCompleted: true },
    { label: "APPOINTMENT", isCompleted: false },
    { label: "CONFIRM", isCompleted: false }
  ];

  return (
    <div className="min-h-screen">
      <div className="flex items-center gap-4 mb-8">
        <SettingOutlined style={{ fontSize: 20 }} />
        <Title level={3} className="text-black mb-0">
          Capacity Settings
        </Title>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="mb-4">
          <Title level={4} className="text-black mb-1">
            Capacity Provider Settings
          </Title>
          <p className="text-gray-500 text-sm mt-1">
            Choose whether to rely on ServiceTitan capacity or AutoBot's custom capacity.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="text-sm font-semibold text-black block">
              {organizationData?.organization_provider === 'HCP' ?
                "Do you want to use HouseCall Timeslots?" :
                "Is ServiceTitan Capacity Configured?"}
            </label>
            <p className="text-gray-500 text-sm mt-1">
              {organizationData?.organization_provider === 'HCP' ?
                "Enable to use HouseCall Pro's built-in scheduling system" :
                "Enable to use ServiceTitan's capacity management system"}
            </p>
          </div>
          <Switch
            className="ml-4"
            checked={isSTCapacitySet}
            onChange={handleSwitchChange}
          />
          <Spin spinning={loading} fullscreen />
        </div>

      </div>

      {organizationData?.organization_provider !== 'HCP' && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-4">
            <Title level={4} className="text-black mb-1">
              Capacity Calculation Settings
            </Title>
            <p className="text-gray-500 text-sm mt-1">
              Control how AutoBot calculates and surfaces availability.
            </p>
          </div>
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-sm font-semibold block text-black">
                  Show Next Available Slot
                </label>
                <p className="text-gray-500 text-sm mt-1">
                  Allow AutoBot to highlight the next available appointment based on the available capacity.
                </p>
              </div>
              <Switch
                className="ml-4"
                checked={showNextAvailableSlot}
                onChange={handleShowNextAvailableSlotChange}
              />
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className={`text-sm font-semibold block`}>
                    Enable Unthrottled Capacity
                  </label>
                  <p className={`text-sm mt-1 text-gray-500`}>
                    When enabled, there will be no limit on the number of bookings that can be made.
                  </p>
                </div>
                <Switch
                  className="ml-4"
                  checked={isCapacityUnthrottled}
                  onChange={handleUnthrottledSwitchChange}
                  disabled={isSTCapacitySet}
                />
              </div>
              <CustomCssTransition
                in={isSTCapacitySet}
                timeout={300}
                classNames="glimpse"
                unmountOnExit
              >
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined style={{ fontSize: 16 }} />}
                  message="Unthrottled capacity is only available when using AutoBot's custom capacity."
                  className="mt-2"
                  style={{
                    padding: "0.5rem",
                    minWidth: "28rem",
                    width: "fit-content",
                    display: "inline-flex",
                    alignItems: "center"
                  }}
                />
              </CustomCssTransition>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className={`text-sm font-semibold block `}>
                    Exclude Non Job Events
                  </label>
                  <p className={`text-sm mt-1 text-gray-500`}>
                    When enabled, non job events (meetings, etc.) will be excluded from the available capacity.
                  </p>
                </div>
                <Switch
                  className="ml-4"
                  checked={showNonJobsEvents}
                  onChange={handleShowNonJobsEventsChange}
                  disabled={isSTCapacitySet}
                />
              </div>

              <CustomCssTransition
                in={isSTCapacitySet}
                timeout={300}
                classNames="glimpse"
                unmountOnExit
              >
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined style={{ fontSize: 16 }} />}
                  message="Excluding non job events is only available when using AutoBot's custom capacity."
                  className="mt-2"
                  style={{
                    padding: "0.5rem",
                    minWidth: "28rem",
                    width: "fit-content",
                    display: "inline-flex",
                    alignItems: "center"
                  }}
                />
              </CustomCssTransition>
            </div>

            <div className="border-t pt-6">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-black block">
                      Time Slot Buffer Settings
                    </label>
                    <p className="text-gray-500 text-sm mt-1">
                      Control booking time restrictions and buffer requirements.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-sm font-semibold text-black block">
                        <span className="flex items-center gap-2">
                          Do not allow bookings inside of a booking window that is already begun
                          <Tooltip title="Prevents customers from booking a time slot if that window has already started. For example, once a 9 AM–11 AM window begins, no new bookings can be scheduled inside it.">
                            <InfoCircleOutlined style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)" }} />
                          </Tooltip>
                        </span>
                      </label>
                    </div>
                    <Switch
                      className="ml-4"
                      checked={preventPastSlotSelection}
                      onChange={handlePreventPastSlotSelectionChange}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold text-black block">
                          <span className="flex items-center gap-2">
                            Do not allow a booking within "X" hours of the beginning of a booking window
                            <Tooltip title="Blocks customers from booking too close to the start time. For example, with 2 hours set, a 1 PM slot can't be booked after 11 AM.">
                              <InfoCircleOutlined style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.45)" }} />
                            </Tooltip>
                          </span>
                        </label>
                      </div>
                      <Switch
                        className="ml-4"
                        checked={requireBufferTime}
                        onChange={handleRequireBufferTimeChange}
                      />
                    </div>

                    <CustomCssTransition
                      in={requireBufferTime}
                      timeout={300}
                      classNames="glimpse"
                      unmountOnExit
                    >
                      <div className="mt-4 pl-4 border-l-2 border-gray-200 w-60">
                        <label className="text-sm font-semibold text-black block mb-2">
                          Buffer Time (Hours)
                        </label>
                        <div className="w-32">
                          <InputNumber
                            min={0}
                            precision={1}
                            value={bufferHours}
                            onChange={handleBufferHoursChange}
                            placeholder="Enter hours"
                            className="text-center"
                          />
                        </div>
                      </div>
                    </CustomCssTransition>
                  </div>
                </div>

                <Alert
                  type="error"
                  showIcon
                  icon={<ExclamationCircleOutlined style={{ fontSize: 16, backgroundColor: "#ffebee" }} />}
                  message="Companies who use all day booking windows are discouraged from using this feature, unless they do not want same day bookings."
                  className="booking-warning-alert"
                  style={{
                    padding: "0.5rem",
                    marginTop: "0.75rem",
                    minWidth: "28rem",
                    width: "fit-content",
                    display: "inline-flex",
                    alignItems: "center",
                    borderLeft: "4px solid #ef9a9a",
                    borderColor: "#ef9a9a"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="mb-6">
          <Title level={4} className="text-black mb-1">
            No Availability Setting
          </Title>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col items-start gap-2">
            <div className="mb-4">
              <label className="text-sm font-semibold text-black block">
                No Availability Message
              </label>
              <p className="text-gray-500 text-sm mt-1">
                This is the text that customers see when no time slot is available on the selected date.
              </p>
            </div>
            <div className="flex items-center gap-2 mb-2 w-full">
              <Switch
                checked={useDefaultText}
                onChange={handleDefaultToggle}
                size="small"
              />
              <span className="text-sm text-gray-600">Use default message</span>
            </div>
            <Input.TextArea
              value={editorContent}
              onChange={handleMessageChange}
              showCount
              maxLength={150}
              rows={4}
              placeholder="Enter message to show when no timeslots are available"
              className="w-full"
              disabled={useDefaultText}
            />

            <div className="mt-6 w-full border-t pt-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1">
                  <label className="text-sm font-semibold text-black block">
                    Enable Request Booking Button
                  </label>
                  <p className="text-gray-500 text-sm mt-1">
                    Allow customers to request a booking when no time slots are available. The button will appear below the no availability message.
                  </p>
                </div>
                <Switch
                  checked={isRequestBookingEnabled}
                  onChange={handleRequestBookingEnabledChange}
                />
              </div>

              <div className={`${!isRequestBookingEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="mt-4 w-full">
                  <div className="mb-4">
                    <label className="text-sm font-semibold text-black block">
                      Request Booking Button Text
                    </label>
                    <p className="text-gray-500 text-sm mt-1">
                      The text displayed on the request booking button when no slots are available.
                    </p>
                  </div>
                  <Input
                    value={requestBookingButtonText}
                    onChange={handleRequestBookingButtonTextChange}
                    showCount
                    maxLength={30}
                    placeholder="Enter button text"
                    className="w-full"
                    disabled={!isRequestBookingEnabled}
                  />
                </div>

                <div className="mt-4 w-full">
                  <div className="mb-4">
                    <label className="text-sm font-semibold text-black block">
                      Request Booking Explainer Text
                    </label>
                    <p className="text-gray-500 text-sm mt-1">
                      Message displayed below the button to explain what happens when clicked.
                    </p>
                  </div>
                  <Input.TextArea
                    value={requestBookingExplainerText}
                    onChange={handleRequestBookingExplainerTextChange}
                    showCount
                    maxLength={200}
                    rows={3}
                    placeholder="Enter explainer text"
                    className="w-full"
                    disabled={!isRequestBookingEnabled}
                  />
                </div>

                <div className="mt-4 w-full">
                  <div className="mb-4">
                    <label className="text-sm font-semibold text-black block">
                      Notification Email Addresses
                    </label>
                    <p className="text-gray-500 text-sm mt-1">
                      Email addresses that will receive notifications when customers request a booking on unavailable dates. All session data (contact info, job details, requested date, equipment info, etc.) will be included in the email.
                    </p>
                  </div>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Add email addresses (press Enter or comma to add)"
                    value={requestBookingNotificationEmails}
                    onChange={handleRequestBookingNotificationEmailsChange}
                    tokenSeparators={[","]}
                    open={false}
                    suffixIcon={null}
                    disabled={!isRequestBookingEnabled}
                  >
                    {requestBookingNotificationEmails.map((email) => (
                      <Option key={email} value={email}>
                        {email}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start gap-2 overflow-auto">
            <div className="mb-4">
              <label className="text-sm font-semibold text-black block">
                Preview
              </label>
              <p className="text-gray-500 text-sm mt-1">
                This shows how your message will appear to customers in the scheduling interface.
              </p>
            </div>
            <style>{poppinsStyle}</style>
            <div className="flex flex-col w-full h-[600px] border rounded-lg p-0 bg-[#F2F0FA] font-[Poppins]">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between bg-white p-2 rounded-t-lg shadow-sm">
                  <div className="flex justify-between items-center gap-2 w-full p-2">
                    <h3 className="text-[12px] font-semibold">CREATE APPOINTMENT</h3>
                    <div className="text-black cursor-pointer rounded-full border-2 border-black p-[2px] text-[10px] w-4 h-4 flex items-center justify-center">
                      <svg viewBox="0 0 16 16" className="w-2.5 h-2.5 stroke-[3]" fill="none" stroke="currentColor">
                        <path d="M4 4L12 12M4 12L12 4" strokeLinecap="round" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="border-b pb-4 py-4 px-6 bg-[#F5F8FB]">
                  <div className="flex items-center gap-4 overflow-x-auto">
                    {steps.map((step, index) => (
                      <React.Fragment key={step.label}>
                        {index > 0 && (
                          <div className="flex-1 border-t border-dashed border-gray-300 mt-3"></div>
                        )}
                        <div ref={step.label === "APPOINTMENT" ? focus : null} className="flex flex-col items-center gap-2">
                          <div className={`w-6 h-6 rounded-full ${step.isCompleted ? 'bg-red-400 text-white' : 'bg-gray-200 text-gray-700'} flex items-center justify-center text-[12px]`}>
                            {step.isCompleted ? '✓' : index + 1}
                          </div>
                          <span className={`text-[12px] ${step.label === "APPOINTMENT" ? 'font-bold' : ''}`}>{step.label}</span>
                        </div>
                      </React.Fragment>
                    ))} 

                  </div>
                </div>
                <div className="overflow-y-auto min-h-0 bg-white rounded-b-lg" ref={mainContentRef}>
                  <div className="flex flex-col justify-center items-center px-6">
                    <div className="flex justify-between items-center text-center mb-2 relative w-full">
                      <div className="flex-1 absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl cursor-pointer">‹</div>
                      <h2 className=" flex-1 text-[32px] leading-[44px] font-[300] py-4 px-2 text-gray-900 font-[Verdana]">June 2025</h2>
                      <div className="flex-1 absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl cursor-pointer">›</div>
                    </div>
                    <div className="grid grid-cols-7 gap-6 mb-4 text-center w-full">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day, index) => (
                        <div key={index} className="w-10 h-10 flex items-center justify-center text-sm font-medium">
                          {day}
                        </div>
                      ))}
                      {Array.from({ length: 35 }).map((_, i) => {
                        const day = i + 1;
                        const isSelected = day === 22;
                        const isGrayed = day > 28;
                        return (
                          <div key={i}
                            className={`w-10 h-6 flex items-center justify-center text-sm ${isSelected ? 'bg-red-400 text-white rounded-full' :
                              isGrayed ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                            {isGrayed ? day - 28 : day}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className="py-4 block text-sm leading-6 italic tracking-[0.02957rem] text-[rgb(139,0,0)] bg-[#F2F0FA] text-start px-8 font-medium"
                    style={{
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                      lineHeight: '1.5'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        formatPhoneNumber(useDefaultText ? defaultMessage : editorContent),
                        {
                          ALLOWED_TAGS: ['p' , 'a', 'br', 'span'],
                          ALLOWED_ATTR: ['href', 'target', 'style', 'class'],
                          ALLOWED_STYLES: ['color', 'text-decoration', 'margin', 'padding', 'display'],
                          ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                          ADD_ATTR: ['target', 'rel'],
                          ADD_DATA_URI_TAGS: ['a']
                        }
                      )
                    }}
                  />
                  <CustomCssTransition
                    in={isRequestBookingEnabled}
                    timeout={300}
                    classNames="glimpse"
                    unmountOnExit
                  >
                    <div className="px-8 py-4 bg-[#F2F0FA] flex flex-col items-center gap-3">
                      <button
                        className="px-6 py-2.5 bg-red-400 text-white rounded-full shadow-sm hover:bg-red-500 font-medium text-sm transition-colors"
                        style={{ minWidth: 'fit-content' }}
                      >
                        {requestBookingButtonText || defaultRequestBookingButtonText}
                      </button>
                      {requestBookingExplainerText && (
                        <p className="text-xs text-gray-600 text-center max-w-md" style={{ lineHeight: '1.5' }}>
                          {requestBookingExplainerText}
                        </p>
                      )}
                    </div>
                  </CustomCssTransition>
                  <div  className="sticky bottom-0 bg-white border-t p-2 rounded-b-lg shadow-sm">
                    <div className="flex justify-between mx-auto">
                      <button className="px-6 py-2 bg-white border border-gray-300 rounded-full text-gray-700 shadow-sm hover:bg-gray-50">
                        ← Back
                      </button>
                      <button className="px-6 py-2 bg-red-400 text-white rounded-full shadow-sm hover:bg-red-500">
                        Next →
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      <SaveButton isDirty={isDirty} onSubmit={handleSave} loading={loading} />
    </div>
  );
}

export default CapacitySettings;